<template>
	<div class="loadout-manager">
		<div class="lm-selector-panel" v-if="!minimized">
			<div class="lm-label">
				<div class="btn lm-list-add" @click="createLoadout">
					<span class="material-icons">add_circle_outline</span>
				</div>
				<select v-model="selectedLoadout" class="lm-selector">
					<option v-for="loadout in loadoutList" :key="loadout.id"
						:value="loadout"
					>
						{{loadout.name}}
					</option>
				</select>
			</div>
		</div>
		<div class="divider" />
		<div class="loadout scrollable">
			<div class="loadout-container">
				<div class="lm-loadout-name" v-if="!minimized">
					<div class="lm-label">
						<span>Name</span>
						<input type="text"
								pattern="(\w+\s?)?"
								v-model="currentLoadout.name"
								placeholder="insert name here"
							>
					</div>
				</div>
				<div class="lm-card">
					<div class="lm-label">
						<span>Cards</span>
						<div class="lm-char-select">
							<span class="material-icons">person_search</span>
							<div class="lm-char-list">
								<transition-group name="pop-item">
									<div v-for="char in cardsCharacters" :key="char.name" class="lm-char-icon"
										:class="{ 'is-active': currentCharacter === char.name }"
										@click="currentCharacter = char.name"
									>
										<img :src="iconURL(char.icon)" alt="">
									</div>
									<div class="lm-char-icon" key=""
										:class="{ 'is-active': currentCharacter === null, 'has-no-character': !cardsCharacters.length }"
										@click="currentCharacter = null"
									>
										<span class="material-icons">person_off</span>
									</div>
								</transition-group>
							</div>
						</div>
					</div>
					<div class="lm-skill-view-select" :class="`selected-${~~(defaultSkillVew === 'combo')}`">
						<div class="lm-skill-view-bg" />
						<div class="lm-skill-view-opt" @click="defaultSkillVew = 'label'">Skill names</div>
						<div class="lm-skill-view-opt" @click="defaultSkillVew = 'combo'">Skill combos</div>
					</div>
					<div class="lm-card-previews">
						<div class="lm-card-preview-bg">
							<div v-for="i in 3" :key="i" class="lm-card-preview" :class="`card-slot-${i - 1}`">
								<span class="card-bg-text">{{i}}</span>
							</div>
						</div>
						<div class="lm-card-preview-list replace-slots" v-if="cardHeld && edit">
							<transition-group name="reverse-list">
								<div v-for="(card, i) in swapSlotEntries" :key="card"
									class="lm-card-preview" :class="`card-slot-${i}`"
									@click="swapSlot(i)"
								>
									<loadout-card
										:card="card"
										frame-only
									>
										<span class="card-bg-text">{{i + 1}}</span>
									</loadout-card>
								</div>
							</transition-group>
						</div>
						<draggable v-model="currentCards" class="lm-card-preview-list"
							:move="handleMove"
							@end="handleDragEnd"
							fallback-tolerance="10"
							ghost-class="ghost"
							:swap="true"
						>
							<transition-group name="list">
								<loadout-card v-for="(card, i) in currentCards" :key="card"
									:class="`card-slot-${i}`"
									:card="card"
									:default-skill-view="defaultSkillVew"
									@frame-click="removeCard(i)"
									@r5-changed="updateCardR5(i)"
								/>
							</transition-group>
						</draggable>
					</div>
				</div>
				<!-- <div class="lm-skills">
					<div class="lm-label">Skills</div>
				</div> -->
				<div class="lm-stats" v-if="!minimized">
					<div class="lm-label">
						<span>Stats</span>
						<div style="flex: auto;">
							<div v-for="(amount, statName) in currentStats" :key="statName" class="lm-stat">
								<span class="lm-stat-label">{{statName}}</span>
								<span class="lm-stat-amount" :style="{'--weight-amount': amount.weight * 100 + '%' }">{{ amount.value }}</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="divider" />
		<div class="lm-footer">
			<div class="lm-list-btn-group">
				<div  v-if="!minimized" class="btn text-danger round" :disabled="!('_model' in currentLoadout)" @click="deleteLoadout">
					<span class="material-icons">delete</span>
				</div>
				<div class="btn round" :disabled="!hasChanged" @click="resetLoadout">
					<span class="material-icons">settings_backup_restore</span>
				</div>
				<div class="btn round" :disabled="!hasChanged" @click="saveLoadout">
					<span class="material-icons">save</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { cloneDeep } from 'lodash';
import { VueDraggableNext } from 'vue-draggable-next';
import { watch, computed } from 'vue';

import LoadoutCard from '@/components/LoadoutCard.vue';

import {
	icons as characterIcons
} from '@/utils/characters';
import {
	cardHeld,
	loadoutList,
	saveLoadout,
	deleteLoadout,
	loadoutStore
} from '@/utils/LoadoutList';
import {
	MAX_STATS
} from '@/utils/CardsGetter';

const DEFAULT_LOADOUT = {
	name: '',
	cards: [
		{ empty: true },
		{ empty: true },
		{ empty: true }
	]
};

export default {
	name: 'loadout-manager',
	components: {
		LoadoutCard,
		draggable: VueDraggableNext
	},
	props: {
		edit: {
			type: Boolean,
			default: false
		},
		minimized: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			loadoutList,
			currentCharacter: null,
			currentCardsCombo: {},
			currentCards: new Array(3).fill({ empty: true }),
			activeSkills: {},
			defaultSkillVew: 'label',
			swapSlotEntries: [
				{ empty: true },
				{ empty: true },
				{ empty: true }
			],
			currentLoadout: {
				name: '',
				cards: []
			}
		};
	},
	computed: {
		cardHeld() {
			return cardHeld.value;
		},
		hasChanged() {
			const model = this.currentLoadout._model ?? cloneDeep(DEFAULT_LOADOUT);
			return (model.name !== this.currentLoadout.name
				|| this.currentLoadout.cards.length !== model.cards.length
				|| !!this.currentLoadout.cards.find((c1, i) => {
					const c2 = model.cards[i];
					return ((c1.empty !== c2.empty)
						|| (!c1.empty && (c1.r5 !== c2.r5 || c1.model.id !== c2.model.id)));
				}));
		},
		selectedLoadout: {
			get() {
				return this.currentLoadout._model;
			},
			set(loadout) {
				loadoutStore.set(loadout);
			}
		},
		allCards() {
			return this.currentLoadout.cards.filter(c => !c.empty);
		},
		currentStats() {
			const stats = {
				atk: { value: 0, weight: 0 },
				def: { value: 0, weight: 0 },
				hp: { value: 0, weight: 0 }
			};
			for (const card of this.allCards) {
				for (const stat of [ 'atk', 'def', 'hp' ]) {
					if (typeof(card.model.stats[stat]) == 'number') {
						stats[stat].value += card.model.stats[stat];
						stats[stat].weight = stats[stat].value / (MAX_STATS[stat] * 3);
					}
				}
			};
			return stats;
		},
		cardsCharacters() {
			const characters = [];
			for (const c of this.allCards) {
				const character = c.model.skills.find(s => (typeof(s.requirement) == 'string'))?.requirement;
				if (character && !characters.find(c => c.name === character)) {
					characters.push({
						name: character,
						icon: characterIcons[character] ?? '/8/81/Randomly_draw_hidden_skill.png'
					});
				}
			}
			return characters;
		}
	},
	watch: {
		currentCharacter: 'updateAllCards',
		currentCards(cards, oldCards) {
			if (this.cardHeld) {
				const swapModel = cards.map(c => {
					const cardIndex = oldCards.findIndex(c2 => c._model === c2._model);
					return this.swapSlotEntries[cardIndex];
				});
				for (const i in swapModel)
					this.swapSlotEntries[i] = swapModel[i];
			}
			for (let i = 0; i < cards.length; i++)
				this.placeCard(i, cards[i]._model);
			this.updateAllCards();
		},
		cardsCharacters(characters) {
			this.currentCharacter = characters[0]?.name ?? null;
		}
	},
	mounted() {
		this.setSwapSlotEntries();
		this.setCurrentLoadout();
	},
	beforeUnmount() {
		cardHeld.value && (cardHeld.value = null);
		loadoutStore.reset();
	},
	methods: {
		saveLoadout() {
			cardHeld.value && (cardHeld.value = null);
			saveLoadout();
		},
		iconURL(pathToIcon) {
			return `https://static.wikia.nocookie.net/rockman_x_dive/images${pathToIcon}`;
		},
		updateCardCombo() {
			this.currentCardsCombo = this.currentLoadout.cards
				.reduce((comboObj, c) => {
					if (!c.empty) {
						const typeLetter = 'RYBG'.charAt(c.model.type);
						if (!(typeLetter in comboObj))
							comboObj[typeLetter] = 0;
						comboObj[typeLetter]++;
					}
					return comboObj;
				}, {});
		},
		updateCardSkill(card, skillIndex) {
			const cardSkill = card.skills[skillIndex];
			const c = card._model;
			const s = cardSkill._model;
			let isActive = false;
			let skillLevel = card.baseSkillLevel;
			if (skillLevel === 2 && !('II' in s.def))
				skillLevel--;
			if (c.r5)
				skillLevel++;

			if (!(s.name in this.activeSkills))
				this.activeSkills[s.name] = [ ];

			if (!this.activeSkills[s.name].includes(skillLevel)) {
				if (typeof(s.requirement) != 'string') { // not a character
					const comboObj = cloneDeep(this.currentCardsCombo);
					isActive = true;
					for (const letter of [ 'R', 'G', 'B', 'Y' ]) {
						if ((comboObj[letter] ?? 0) - (s.requirement[letter] ?? 0) < 0) {
							isActive = false;
							break;
						}
					}
				} else if (this.currentCharacter === s.requirement)
					isActive = true;
				this.activeSkills[s.name].push(skillLevel);
			}
			Object.assign(cardSkill, {
				level: skillLevel,
				name: s.name,
				isActive
			});
		},
		updateCardSkills(card) {
			for (const i in card.skills)
				this.updateCardSkill(card, i);
		},
		updateCard(card) {
			if (!card.empty)
				this.updateCardSkills(card);
		},
		updateAllCards() {
			this.activeSkills = {};
			for (const card of this.currentCards)
				this.updateCard(card);
		},
		modelToSlotCard(cardModel, skipSkillsUpdate = false) {
			const { model } = cardModel;
			if (cardModel.empty)
				return { _model: cardModel, empty: true };
			const slotCard = {
				_model: cardModel,
				r5: cardModel.r5,
				baseSkillLevel: Math.max(1, model.rank),
				icon: model.icon,
				id: model.id,
				rank: model.rank,
				type: model.type,
				skills: model.skills.map(s => ({
					_model: s,
					icon: s.icon
				}))
			};
			if (!skipSkillsUpdate)
				this.updateCardSkills(slotCard);
			return slotCard;
		},
		initCardAt(cardIndex, cardModel, skipSkillsUpdate = false) {
			this.currentCards[cardIndex] = this.modelToSlotCard(cardModel, skipSkillsUpdate);
		},
		initCurrentCards() {
			this.activeSkills = {};
			this.updateCardCombo();
			for (const i in this.currentCards)
				this.initCardAt(i, this.currentLoadout.cards[i]);
		},
		setCurrentLoadout() {
			const currentLoadout = loadoutStore.getState();
			this.currentLoadout = currentLoadout;
			this.initCurrentCards();
			for (const i in this.currentLoadout.cards) {
				watch(
					computed(() => this.currentLoadout.cards[i]),
					() => {
						if (this.currentLoadout.cards[i] === this.currentCards[i]._model)
							return;
						this.updateCardCombo();
						this.initCardAt(i, this.currentLoadout.cards[i], true);
						this.updateAllCards();
					}
				);
			}
		},
		setSwapSlotEntries() {
			if (this.cardHeld) {
				for (const i in this.swapSlotEntries)
					this.swapSlotEntries[i] = this.modelToSlotCard({ r5: false, model: this.cardHeld });
			}
		},
		swapSlot(slotId) {
			const cardToPlace = this.swapSlotEntries[slotId];
			this.swapSlotEntries[slotId] = this.currentCards[slotId];
			this.placeCard(slotId, cardToPlace._model);
		},
		placeCard(cardAtIndex, newCard) {
			loadoutStore.placeCard(cardAtIndex, newCard);
		},
		removeCard(slotId) {
			const cardHeld = this.cardHeld;
			if (cardHeld) {
				const currentModel = this.currentCards[slotId]._model.model;
				const swapModel = this.swapSlotEntries[slotId]._model.model;
				if (currentModel.empty || [ currentModel, swapModel ].includes(cardHeld))
					return this.swapSlot(slotId);
			}
			loadoutStore.removeCard(slotId);
		},
		updateCardR5(cardIndex) {
			loadoutStore.toggleCardR5(cardIndex);
			this.currentCards[cardIndex].r5 = this.currentLoadout.cards[cardIndex].r5;
			this.updateAllCards();
		},
		handleDragEnd() {
			if (this.futureItem !== -1 && this.movingIndex !== -1) {
				const futureItem = this.currentCards[this.futureIndex];
				const movingItem = this.currentCards[this.movingIndex];
				const _items = Object.assign([], this.currentCards);
				_items[this.futureIndex] = movingItem;
				_items[this.movingIndex] = futureItem;
				this.currentCards = _items;
			}
			this.futureIndex = this.movingIndex = -1;
		},
		handleMove(e) {
			const { index, futureIndex } = e.draggedContext;
			this.movingIndex = index;
			this.futureIndex = futureIndex;
			return false; // disable sort
		},
		createLoadout() {
			loadoutStore.clear();
		},
		resetLoadout() {
			this.setSwapSlotEntries();
			loadoutStore.reset();
		},
		deleteLoadout() {
			deleteLoadout();
		}
	}
};
</script>

<style lang="scss">
.loadout-manager {
	user-select: none;
	display: flex;
	flex-direction: column;
	justify-items: center;
	align-items: center;
	width: 100%;
	height: 100%;
	> *:not(.divider) {
		padding: 10px;
	}
	.divider {
		margin: 5px 0;
	}
	.lm-selector-panel {
		.lm-label {
			margin: 0;
		}
		.lm-selector {
			flex: auto;
			border-radius: 5px;
			background: #424242;
			color: white;
			font-weight: 600;
			text-align: center;
			&:focus { outline: none; }
		}
	}
	.lm-list-add {
    min-width: fit-content;
    padding: 4px;
    border-radius: 50%;
	}
	.lm-list-btn-group {
		display: flex;
		justify-content: space-between;
		padding: 0 20px;
		>.btn {
			min-width: fit-content;
			padding: 8px;
			>.material-icons {
				font-size: 32px;
			}
		}
	}
	.loadout {
		flex: auto;
		.loadout-container {
			>*:not(:last-child) {
				margin-bottom: 20px;
			}
			.lm-skill-view-select {
				width: 100%;
				display: flex;
				color: white;
				border-radius: 4px;
				background: #424242;
				margin-bottom: 10px;
				border: 1px solid #fff;
				box-shadow: inset 0 0 6px #007;
				position: relative;
				overflow: hidden;
				.lm-skill-view-bg {
					position: absolute;
					width: 50%;
					height: 100%;
					left: 0;
					background: var(--blue);
					transition: left ease-out 150ms;
					z-index: 0;
				}
				&.selected-1 {
					.lm-skill-view-bg {
						left: 50%;
					}
				}
				.lm-skill-view-opt {
					z-index: 1;
					width: 50%;
					text-align: center;
					padding: 4px 0;
					&:last-child {
						border-left: 1px solid #fff;
					}
				}
			}
			.lm-card-previews {
				position: relative;
				display: flex;
				grid-template-rows: repeat(auto-fit, minmax(100px, 1fr));
				width: 100%;
				height: calc(108px * 3 + 22px);
				overflow: hidden;
				>.lm-card-preview-list {
					position: relative;
					&.replace-slots {
						width: 100px;
						cursor: pointer;
						@media (max-width: 399px) {
							width: 50px;
						}
						.lm-card-preview {
							overflow: unset;
							filter: brightness(0.75);
						}
					}
					&:not(.replace-slots) {
						width: 100%;
						flex: auto;
					}
				}
				.lm-card-preview-bg {
					position: absolute;
					inset: 0;
				}
				.lm-card-preview {
					position: absolute;
					&.card-slot-0 {
						top: 0;
					}
					&.card-slot-1 {
						top: 118px;
					}
					&.card-slot-2 {
						top:236px;
					}
				}
			}
		}
	}
	.lm-stats {
		.lm-stat {
			width: 100%;
			display: flex;
			color: white;
			&:not(:last-child) {
				margin-bottom: 8px;
			}
			.lm-stat-label {
				width: 40px;
			}
			.lm-stat-amount {
				flex: auto;
				text-align: center;
				background: linear-gradient(
					to right,
					#00ff51 0%,
					green var(--weight-amount),
					#335 var(--weight-amount),
					#424242 calc(var(--weight-amount) + 6%)
				);
				border: 1px solid #fff7;
				border-radius: 4px;
				box-shadow: inset 0 0 4px #000;
			}
		}
	}
	> * {
		width: 100%;
	}
	.lm-label {
		margin-bottom: 10px;
		color: white;
		font-weight: 700;
		font-style: oblique;
		text-transform: uppercase;
		display: flex;
		justify-content: space-between;
		align-items: center;
		>span:first-child {
			min-width: 70px;
			padding-right: 10px;
		}
		>input {
			flex: auto;
			padding-left: 5px;
			border-radius: 5px;
			box-shadow: inset 0 0 4px #000;
			overflow: hidden;
			border: none;
		}
		.lm-char-select {
			display: flex;
			align-items: center;
			.lm-char-list {
				display: flex;
				border-radius: 4px;
				margin-left: 10px;
				.lm-char-icon {
					width: 50px;
					height: 50px;
					display: flex;
					align-items: center;
					justify-content: center;
					background: var(--light-blue);
					border-radius: 4px;
					padding: 0;
					transition: all ease-out 150ms;
					overflow: hidden;
					z-index: 1;
					transition: all 150ms ease-out;
					&:not(:first-child) {
						margin-left: 5px;
					}
					&:not(.is-active) {
						filter: brightness(0.5);
						padding: 4px;
						background: var(--darker-blue);
						cursor: pointer;
					}
					&.has-no-character {
						filter: grayscale(1);
					}
					>img {
						width: 100%;
					}
					>.material-icons {
						font-size: 30px;
					}
				}
			}
		}
	}
}

.reverse-list-move, /* apply transition to moving elements */
.reverse-list-enter-active,
.reverse-list-leave-active {
		pointer-events: none;
		z-index: 999;
		transition: all 0.5s ease;
}

.reverse-list-enter-from,
.reverse-list-leave-to {
	opacity: 0;
	transform: translateX(50%);
}

.list-leave-active {
	position: absolute;
}

// draggable class
.ghost {
	transition: all ease-out 50ms;
	opacity: 0.5;
}

// transition-group class
/* == POP-ITEM == */
.pop-item-move, /* apply transition to moving elements */
.pop-item-enter-active,
.pop-item-leave-active {
	pointer-events: none;
	transition: all 0.5s ease;
	z-index: 0;
}

.pop-item-enter-from,
.pop-item-leave-to {
	opacity: 0;
}

.pop-item-enter-from {
	transform: scale(0);
}
.pop-item-leave-to {
	transform: scale(1);
}

/* == LIST == */
.list-move,
.list-enter-active,
.list-leave-active {
	pointer-events: none;
	z-index: 999;
	transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
	opacity: 0;
	transform: translateX(-50%);
}

/* COMMON */
/* ensure leaving items are taken out of layout flow so that moving
	animations can be calculated correctly. */
.pop-item-leave-active,
.list-leave-active {
	position: absolute;
}

</style>
